/** Wetter + Tipps vom Server (alle paar Minuten neu). */
import { useEffect, useState } from 'react';
import type { Advice, ClimateSummary, WeatherData } from '@pflanzen/shared';
import { api, path, getToken } from './api';

export interface WeatherView {
  weather: WeatherData;
  advice: Advice[];
  climate: ClimateSummary;
  timelines: Record<string, { time: string; onPlants: boolean; strong: boolean }[]>;
}

let cache: WeatherView | null = null;
let cacheAt = 0;
let inflight: Promise<WeatherView | null> | null = null;
const subs = new Set<(w: WeatherView | null) => void>();

export async function loadWeather(force = false): Promise<WeatherView | null> {
  if (!force && cache && Date.now() - cacheAt < 5 * 60_000) return cache;
  if (inflight) return inflight;
  if (!(await getToken())) return null;
  inflight = api<WeatherView>('GET', path('weather'))
    .then((w) => {
      cache = w;
      cacheAt = Date.now();
      for (const s of subs) s(w);
      return w;
    })
    .catch(() => cache)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function useWeather(): { data: WeatherView | null; reload: () => void } {
  const [data, setData] = useState<WeatherView | null>(cache);
  useEffect(() => {
    subs.add(setData);
    void loadWeather().then(setData);
    const t = setInterval(() => void loadWeather(), 10 * 60_000);
    return () => {
      subs.delete(setData);
      clearInterval(t);
    };
  }, []);
  return { data, reload: () => void loadWeather(true) };
}
